import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Drawer,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Collapse,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  ExpandMore,
  ExpandLess,
  CloudUpload,
  CloudDownload,
} from '@mui/icons-material';
import { api } from "../context/AuthContext";
import ConfirmModal from "../components/ConfirmModal";

const BPO_EXAMPLES = [
  'Communication Skills', 'Customer Service', 'Voice Process', 'Non-Voice Process',
  'Email Support', 'Chat Support', 'Sales', 'Collections', 'Adaptability',
  'Typing Speed', 'Active Listening', 'Problem Solving', 'Conflict Resolution', 'Shift Flexibility'
];

function Technologies() {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [filterStatus, setFilterStatus] = useState('All Status');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formError, setFormError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Technical');

  // Pagination state
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
    subTechnologies: []
  });
  const [activeTab, setActiveTab] = useState(0);
  const [editingSubTech, setEditingSubTech] = useState(null);
  const [newSubTechName, setNewSubTechName] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const isBPO = selectedCategory === 'Non-Technical';

  const L = {
    itemName: isBPO ? 'Primary Skill' : 'Technology',
    itemNamePlural: isBPO ? 'Primary Skills' : 'Technologies',
    subItemName: isBPO ? 'Sub-Skill' : 'Sub-Technology',
    subItemNamePlural: isBPO ? 'Sub-Skills' : 'Sub-Technologies',
    namePlaceholder: isBPO ? 'Enter primary skill' : 'Enter technology name',
    descPlaceholder: isBPO ? 'Brief description of the skill...' : 'Brief description of the technology...',
    fieldLabel: isBPO ? 'PRIMARY SKILL NAME *' : 'TECHNOLOGY NAME *',
    nameError: isBPO ? 'Primary Skill Name is mandatory' : 'Technology Name is mandatory',
    searchPlaceholder: isBPO ? 'Search primary skill...' : 'Search technology...',
    subInputPlaceholder: isBPO ? 'Enter sub-skill name...' : 'Enter sub-technology name...',
    quickAddLabel: isBPO ? 'Add Sub-Skill' : 'Add Sub-Tech',
    quickAddSubtext: isBPO ? 'Add a new sub-skill' : 'Add a new variant',
    noSubItemsRow: isBPO ? 'No sub-skills added' : 'No sub-technologies added',
    noSubItemsDrawer: isBPO ? 'No sub-skills added yet' : 'No sub-technologies added yet',
    countText: (n) => `${n} ${isBPO ? 'sub-skills' : 'sub-technologies'}`,
    paginationUnit: isBPO ? 'skills' : 'technologies',
    emptyState: isBPO ? "No primary skills available. Click 'Add Primary Skill' to create one." : "No technologies available. Click 'Add Technology' to create one.",
    drawerTitle: (editing) => editing
      ? `Edit ${isBPO ? 'Primary Skill' : 'Technology'}`
      : `Add ${isBPO ? 'Primary Skill' : 'Technology'}`,
    subTabLabel: (n) => `${isBPO ? 'Sub-Skills' : 'Sub-Technologies'} (${n})`,
    tableColItem: isBPO ? 'PRIMARY SKILL' : 'TECHNOLOGY',
    tableColSub: isBPO ? 'SUB-SKILLS' : 'SUB-TECHNOLOGIES',
    sectionTitle: (n) => `${isBPO ? 'Primary Skills' : 'Technologies'} (${n})`,
    sectionSubtitle: isBPO
      ? 'Organize primary skills and their sub-skills'
      : 'Organize technologies and their sub-technologies',
    pageSubtitle: isBPO
      ? 'Manage primary skills and their sub-skills'
      : 'Manage technologies and their sub-technologies',
    exportFilename: isBPO ? 'bpo_skills_export' : 'technologies_export',
    exportHeaders: isBPO
      ? ['Primary Skill Name', 'Status', 'Sub-Skills', 'Created Date']
      : ['Technology Name', 'Status', 'Sub-Technologies', 'Created Date'],
    deleteDialogTitle: isBPO ? 'Delete Primary Skill' : 'Delete Technology',
    deleteSubDialogTitle: isBPO ? 'Delete Sub-Skill' : 'Delete Sub-Technology',
    alreadyExistsError: isBPO
      ? 'This primary skill already exists. Please use a different name.'
      : 'This technology already exists. Please use a different name.',
    saveError: (msg) => msg || `Error saving ${isBPO ? 'primary skill' : 'technology'}. Please ensure names are unique.`,
    subAddError: (msg) => msg || `Error adding ${isBPO ? 'sub-skill' : 'sub-technology'}. Duplicate names are not allowed.`,
  };

  useEffect(() => {
    fetchTechnologies();

    const handleHeaderSearch = (e) => {
      if (e.detail !== undefined) {
        setSearchTerm(e.detail);
      }
    };
    window.addEventListener('header-search', handleHeaderSearch);
    return () => window.removeEventListener('header-search', handleHeaderSearch);
  }, []);

  const fetchTechnologies = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get('/technologies');
      setTechnologies(res.data);
    } catch (err) {
      console.error('Error fetching technologies:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const handleToggleExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenDrawer = (tech = null) => {
    setFormError('');
    if (tech) {
      setEditingTech(tech);
      setFormData({
        name: tech.technology_name,
        description: tech.description || '',
        status: tech.status || 'Active',
        subTechnologies: tech.sub_technologies || []
      });
    } else {
      setEditingTech(null);
      setFormData({
        name: '',
        description: '',
        status: 'Active',
        subTechnologies: []
      });
    }
    setActiveTab(0);
    setDrawerOpen(true);
  };

  const handleSaveTech = async () => {
    try {
      setFormError('');
      if (!formData.name.trim()) {
        setFormError(L.nameError);
        return;
      }

      if (editingTech) {
        await api.put(`/technologies/${editingTech.id}`, {
          name: formData.name.trim(),
          description: formData.description,
          status: formData.status
        });
      } else {
        const res = await api.post('/technologies', {
          name: formData.name.trim(),
          description: formData.description,
          status: formData.status,
          category: selectedCategory
        });

        if (formData.subTechnologies.length > 0) {
          for(const sub of formData.subTechnologies) {
            await api.post(`/technologies/${res.data.id}/sub`, {
              name: sub.sub_technology_name.trim(),
              status: sub.status
            });
          }
        }
        setPage(0);
      }

      setDrawerOpen(false);
      await fetchTechnologies(true);
      showSuccess(`${L.itemName} ${editingTech ? 'updated' : 'added'} successfully`);
    } catch (err) {
      console.error('Error saving technology:', err);
      if (err.response?.status === 409 || err.response?.data?.message?.includes('already exists')) {
        setFormError(L.alreadyExistsError);
        return;
      }
      setSnackbar({
        open: true,
        message: L.saveError(err.response?.data?.message),
        severity: 'error'
      });
    }
  };

  const handleDeleteTech = async (id) => {
    try {
      await api.delete(`/technologies/${id}`);
      await fetchTechnologies(true);
      showSuccess(`${L.itemName} deleted successfully`);
    } catch (err) {
      console.error('Error deleting technology:', err);
      setSnackbar({ open: true, message: `Error deleting ${L.itemName.toLowerCase()}`, severity: 'error' });
    }
  };

  const handleConfirmDeleteTech = (tech) => {
    setConfirmDialog({
      open: true,
      title: L.deleteDialogTitle,
      message: `Are you sure you want to delete ${tech.technology_name}?`,
      onConfirm: () => {
        handleDeleteTech(tech.id);
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };

  const handleAddSubTech = async () => {
    const trimmedName = newSubTechName.trim();
    if (!trimmedName) return;

    if (editingTech) {
      try {
        await api.post(`/technologies/${editingTech.id}/sub`, {
          name: trimmedName,
          status: 'Active'
        });
        const res = await api.get(`/technologies/${editingTech.id}`);
        setFormData(prev => ({ ...prev, subTechnologies: res.data.sub_technologies }));
        setNewSubTechName('');
        setExpandedRows(prev => ({ ...prev, [editingTech.id]: true }));
        await fetchTechnologies(true);
        showSuccess(`${L.subItemName} added successfully`);
      } catch (err) {
        console.error('Error adding sub-technology:', err);
        setSnackbar({
          open: true,
          message: L.subAddError(err.response?.data?.message),
          severity: 'error'
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        subTechnologies: [...prev.subTechnologies, { sub_technology_name: trimmedName, status: 'Active' }]
      }));
      setNewSubTechName('');
    }
  };

  const handleEditSubTech = (sub) => {
    setEditingSubTech(sub);
    setNewSubTechName(sub.sub_technology_name);
  };

  const handleUpdateSubTech = async () => {
    if (!newSubTechName.trim() || !editingSubTech) return;

    if (editingTech && editingSubTech.id) {
      try {
        await api.put(`/technologies/sub/${editingSubTech.id}`, {
          name: newSubTechName.trim(),
          status: editingSubTech.status
        });
        const res = await api.get(`/technologies/${editingTech.id}`);
        setFormData(prev => ({ ...prev, subTechnologies: res.data.sub_technologies }));
        setNewSubTechName('');
        setEditingSubTech(null);
        await fetchTechnologies(true);
        showSuccess(`${L.subItemName} updated successfully`);
      } catch (err) {
        console.error('Error updating sub-technology:', err);
        setSnackbar({ open: true, message: `Error updating ${L.subItemName.toLowerCase()}`, severity: 'error' });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        subTechnologies: prev.subTechnologies.map(sub =>
          sub === editingSubTech ? { ...sub, sub_technology_name: newSubTechName.trim() } : sub
        )
      }));
      setNewSubTechName('');
      setEditingSubTech(null);
    }
  };

  const handleDeleteSubTech = async (subId, index) => {
    if (editingTech && subId) {
      try {
        await api.delete(`/technologies/sub/${subId}`);
        const res = await api.get(`/technologies/${editingTech.id}`);
        setFormData(prev => ({ ...prev, subTechnologies: res.data.sub_technologies }));
        await fetchTechnologies(true);
        showSuccess(`${L.subItemName} deleted successfully`);
      } catch (err) {
        console.error('Error deleting sub-technology:', err);
        setSnackbar({ open: true, message: `Error deleting ${L.subItemName.toLowerCase()}`, severity: 'error' });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        subTechnologies: prev.subTechnologies.filter((_, i) => i !== index)
      }));
    }
  };

  const handleConfirmDeleteSubTech = (sub, index) => {
    setConfirmDialog({
      open: true,
      title: L.deleteSubDialogTitle,
      message: `Are you sure you want to delete ${sub.sub_technology_name}?`,
      onConfirm: () => {
        handleDeleteSubTech(sub.id, index);
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };

  const filteredTechs = useMemo(() => {
    return technologies.filter(tech => {
      const techCategory = tech.category || 'Technical';
      if (techCategory !== selectedCategory) return false;
      const lowerSearch = searchTerm.toLowerCase();
      const matchesTechName = tech.technology_name.toLowerCase().includes(lowerSearch);
      const matchesSubTechName = tech.sub_technologies?.some(st =>
        st.sub_technology_name.toLowerCase().includes(lowerSearch)
      );
      const matchesStatus = filterStatus === 'All Status' || tech.status === filterStatus;
      return (matchesTechName || matchesSubTechName) && matchesStatus;
    });
  }, [technologies, searchTerm, filterStatus, selectedCategory]);

  const paginatedTechs = useMemo(() => {
    return filteredTechs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTechs, page]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterStatus, selectedCategory]);

  const handleExport = () => {
    const rows = filteredTechs.map(tech => [
      tech.technology_name,
      tech.status,
      tech.sub_technologies?.map(st => st.sub_technology_name).join('; ') || '',
      new Date(tech.created_at || Date.now()).toLocaleDateString()
    ]);

    const csvContent = [
      L.exportHeaders.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${L.exportFilename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n').map(row => row.split(',').map(cell => cell.replace(/^"(.*)"$/, '$1').trim()));

        const dataRows = rows.slice(1).filter(row => row.length >= 1 && row[0]);

        let successCount = 0;
        let skipCount = 0;

        for (const row of dataRows) {
          const [techName, status, subTechsStr] = row;

          const exists = technologies.some(t => t.technology_name.toLowerCase() === techName.toLowerCase());
          if (exists) {
            skipCount++;
            continue;
          }

          try {
            const res = await api.post('/technologies', {
              name: techName,
              status: status || 'Active',
              description: '',
              category: selectedCategory
            });

            if (subTechsStr) {
              const subs = subTechsStr.split(';').map(s => s.trim()).filter(Boolean);
              for (const subName of subs) {
                await api.post(`/technologies/${res.data.id}/sub`, {
                  name: subName,
                  status: 'Active'
                });
              }
            }
            successCount++;
          } catch (err) {
            console.error(`Error importing tech ${techName}:`, err);
          }
        }

        showSuccess(`Import completed. ${successCount} added, ${skipCount} skipped (duplicates).`);
        await fetchTechnologies(true);
      } catch (err) {
        console.error('Error reading file:', err);
        setSnackbar({ open: true, message: 'Error processing file. Please ensure it is a valid CSV.', severity: 'error' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleReorderSubTechs = async (direction, index) => {
    const newSubs = [...formData.subTechnologies];
    if (direction === 'up' && index > 0) {
      [newSubs[index], newSubs[index - 1]] = [newSubs[index - 1], newSubs[index]];
    } else if (direction === 'down' && index < newSubs.length - 1) {
      [newSubs[index], newSubs[index + 1]] = [newSubs[index + 1], newSubs[index]];
    } else {
      return;
    }

    setFormData({ ...formData, subTechnologies: newSubs });

    if (editingTech) {
      try {
        await api.post(`/technologies/${editingTech.id}/reorder`, {
          orderedIds: newSubs.map(s => s.id)
        });
        fetchTechnologies();
      } catch (err) {
        console.error('Error saving new order:', err);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    Technologies
                  </Typography>
          <Typography variant="body2" color="textSecondary">{L.pageSubtitle}</Typography>
        </Box>
      </Box>

      {/* Category Selector */}
      <Box display="flex" gap={2} mb={3}>
        {[
          { key: 'Technical', label: 'Technical', sub: 'Software technologies & frameworks' },
          { key: 'Non-Technical', label: 'Non-Technical (BPO)', sub: 'BPO & communication skills' },
        ].map(({ key, label, sub }) => (
          <Paper
            key={key}
            onClick={() => setSelectedCategory(key)}
            elevation={0}
            sx={{
              p: 2.5,
              cursor: 'pointer',
              border: selectedCategory === key ? '2px solid #1e3a8a' : '1.5px solid #e2e8f0',
              borderRadius: 2,
              flex: 1,
              textAlign: 'center',
              bgcolor: selectedCategory === key ? '#eff6ff' : '#fff',
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#1e3a8a', bgcolor: selectedCategory === key ? '#eff6ff' : '#f8fafc' }
            }}
          >
            <Typography fontWeight={700} color={selectedCategory === key ? '#1e3a8a' : '#334155'} gutterBottom>
              {label}
            </Typography>
            <Typography variant="caption" color="textSecondary">{sub}</Typography>
          </Paper>
        ))}
      </Box>

      <Paper sx={{ p: 0, overflowX: 'auto', borderRadius: 2 }}>
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{L.sectionTitle(filteredTechs.length)}</Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDrawer()}
                sx={{
                  bgcolor: '#1e3a8a',
                  '&:hover': { bgcolor: '#1e3a8a' },
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Add {L.itemName}
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="textSecondary" mb={3}>{L.sectionSubtitle}</Typography>

          <Box display="flex" gap={2} mb={3} alignItems="center">
            <TextField
              size="small"
              placeholder={L.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                window.dispatchEvent(new CustomEvent('tech-page-search', { detail: val }));
              }}
              sx={{
                width: 320,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  bgcolor: '#f8fafc',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box ml="auto">
              <FormControl size="small" sx={{ width: 140 }}>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="All Status">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell>{L.tableColItem}</TableCell>
                  <TableCell>{L.tableColSub}</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell align="right">ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTechs.length > 0 ? paginatedTechs.map((tech) => (
                  <React.Fragment key={tech.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <IconButton size="small" onClick={() => handleToggleExpand(tech.id)}>
                            {expandedRows[tech.id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Typography fontWeight="medium">{tech.technology_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {L.countText(tech.sub_technologies?.length || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tech.status}
                          size="small"
                          sx={{
                            bgcolor: tech.status === 'Active' ? '#f0fdf4' : '#fef2f2',
                            color: tech.status === 'Active' ? '#16a34a' : '#dc2626',
                            fontWeight: 'bold',
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                          <IconButton size="small" onClick={() => handleOpenDrawer(tech)} sx={{ color: '#1e3a8a' }}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => { handleOpenDrawer(tech); setActiveTab(1); }} sx={{ color: '#1e3a8a' }}>
                            <Add fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleConfirmDeleteTech(tech)} sx={{ color: '#ef4444' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded sub-items */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedRows[tech.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, ml: 2 }}>
                            <Table size="small">
                              <TableBody>
                                {tech.sub_technologies?.map((sub) => (
                                  <TableRow key={sub.id}>
                                    <TableCell sx={{ width: '30%' }}>{sub.sub_technology_name}</TableCell>
                                    <TableCell sx={{ width: '40%' }}>—</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={sub.status}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          color: sub.status === 'Active' ? '#16a34a' : '#dc2626',
                                          borderColor: sub.status === 'Active' ? '#16a34a' : '#dc2626',
                                          height: 20,
                                          fontSize: '0.7rem'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <IconButton size="small">
                                        <Edit fontSize="inherit" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {(!tech.sub_technologies || tech.sub_technologies.length === 0) && (
                                  <TableRow>
                                    <TableCell colSpan={5}>
                                      <Typography variant="caption" color="textSecondary">{L.noSubItemsRow}</Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Typography variant="h6" color="textSecondary">{L.emptyState}</Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => handleOpenDrawer()}
                          sx={{ bgcolor: '#1e3a8a' }}
                        >
                          Add {L.itemName}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box p={2} display="flex" justifyContent="space-between" alignItems="center" borderTop="1px solid #e2e8f0">
            <Typography variant="body2" color="textSecondary">
              {filteredTechs.length > 0
                ? `Showing ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredTechs.length)} of ${filteredTechs.length} ${L.paginationUnit} | Page ${page + 1} of ${Math.max(1, Math.ceil(filteredTechs.length / rowsPerPage))}`
                : `Showing 0 ${L.paginationUnit}`}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                disabled={page === 0}
                variant="outlined"
                sx={{ minWidth: 32, p: 0 }}
                onClick={() => setPage(p => p - 1)}
              >
                &lt;
              </Button>
              {[...Array(Math.ceil(filteredTechs.length / rowsPerPage))].map((_, i) => (
                <Button
                  key={i}
                  size="small"
                  variant={page === i ? "contained" : "outlined"}
                  sx={{ minWidth: 32, p: 0, bgcolor: page === i ? '#1e3a8a' : 'transparent' }}
                  onClick={() => setPage(i)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                size="small"
                disabled={page >= Math.ceil(filteredTechs.length / rowsPerPage) - 1}
                variant="outlined"
                sx={{ minWidth: 32, p: 0 }}
                onClick={() => setPage(p => p + 1)}
              >
                &gt;
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Edit/Add Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 480 },
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-4px 0 10px rgba(0,0,0,0.05)'
          }
        }}
      >
        <Box p={3} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #f1f5f9">
          <Typography variant="h6" fontWeight="700" color="#1e293b">
            {L.drawerTitle(editingTech)}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ color: '#64748b' }}>
            <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />
          </IconButton>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 100,
                py: 2
              },
              '& .Mui-selected': { color: '#1e3a8a !important' },
              '& .MuiTabs-indicator': { bgcolor: '#1e3a8a' }
            }}
          >
            <Tab label="Details" />
            <Tab label={L.subTabLabel(formData.subTechnologies.length)} />
          </Tabs>
        </Box>

        <Box p={3} sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {activeTab === 0 && (
            <Box display="flex" flexDirection="column" gap={2.5}>
              {formError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                  {formError}
                </Alert>
              )}

              <Box>
                <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ mb: 1, display: 'block', ml: 0.5 }}>{L.fieldLabel}</Typography>
                <TextField
                  placeholder={L.namePlaceholder}
                  required
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>

              <Box>
                <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ mb: 1, display: 'block', ml: 0.5 }}>DESCRIPTION</Typography>
                <TextField
                  placeholder={L.descPlaceholder}
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>

              <Box>
                <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ mb: 1, display: 'block', ml: 0.5 }}>STATUS</Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box mt={2}>
                <Typography variant="subtitle2" fontWeight="700" color="#475569" gutterBottom>Quick Actions</Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#1e3a8a', transform: 'translateY(-2px)' }
                    }}
                    onClick={() => setActiveTab(1)}
                  >
                    <Add sx={{ color: '#1e3a8a', mb: 1 }} />
                    <Typography variant="body2" display="block" fontWeight="600" color="#1e293b">{L.quickAddLabel}</Typography>
                    <Typography variant="caption" color="textSecondary">{L.quickAddSubtext}</Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#1e3a8a', transform: 'translateY(-2px)' }
                    }}
                  >
                    <input
                      type="file"
                      accept=".csv"
                      style={{ position: 'absolute', top: 0, left: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                      onChange={handleBulkImport}
                    />
                    <CloudUpload sx={{ color: '#1e3a8a', mb: 1 }} />
                    <Typography variant="body2" display="block" fontWeight="600" color="#1e293b">Bulk Import</Typography>
                    <Typography variant="caption" color="textSecondary">Import from CSV</Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#1e3a8a', transform: 'translateY(-2px)' }
                    }}
                    onClick={handleExport}
                  >
                    <CloudDownload sx={{ color: '#1e3a8a', mb: 1 }} />
                    <Typography variant="body2" display="block" fontWeight="600" color="#1e293b">Export Data</Typography>
                    <Typography variant="caption" color="textSecondary">Export to CSV</Typography>
                  </Paper>
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {isBPO && (
                <Box mb={2}>
                  <Typography variant="caption" fontWeight="600" color="#64748b" sx={{ display: 'block', mb: 1 }}>SUGGESTIONS</Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.75}>
                    {BPO_EXAMPLES.filter(ex => !formData.subTechnologies.some(s => s.sub_technology_name === ex)).map(ex => (
                      <Chip
                        key={ex}
                        label={ex}
                        size="small"
                        clickable
                        onClick={() => setNewSubTechName(ex)}
                        sx={{ fontSize: '0.7rem', height: 24, bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box display="flex" gap={1} mb={3}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={L.subInputPlaceholder}
                  value={newSubTechName}
                  onChange={(e) => setNewSubTechName(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                {editingSubTech ? (
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateSubTech}
                      sx={{ bgcolor: '#10b981', textTransform: 'none', borderRadius: '8px' }}
                    >
                      Update
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => { setEditingSubTech(null); setNewSubTechName(''); }}
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleAddSubTech}
                    sx={{ bgcolor: '#1e3a8a', textTransform: 'none', borderRadius: '8px', px: 3 }}
                  >
                    Add
                  </Button>
                )}
              </Box>

              <TableContainer sx={{ border: '1px solid #f1f5f9', borderRadius: '8px' }}>
                <Table size="small">
                  <TableBody>
                    {formData.subTechnologies.map((sub, index) => (
                      <TableRow key={sub.id || index} hover>
                        <TableCell padding="checkbox" sx={{ borderRight: '1px solid #f1f5f9' }}>
                          <Box display="flex" flexDirection="column">
                            <IconButton size="small" onClick={() => handleReorderSubTechs('up', index)} disabled={index === 0} sx={{ p: 0.25 }}>
                              <ExpandLess fontSize="inherit" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleReorderSubTechs('down', index)} disabled={index === formData.subTechnologies.length - 1} sx={{ p: 0.25 }}>
                              <ExpandMore fontSize="inherit" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{sub.sub_technology_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={sub.status}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              borderColor: sub.status === 'Active' ? '#bbf7d0' : '#fecaca',
                              bgcolor: sub.status === 'Active' ? '#f0fdf4' : '#fef2f2',
                              color: sub.status === 'Active' ? '#16a34a' : '#dc2626'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditSubTech(sub)} sx={{ color: '#64748b' }}>
                            <Edit fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleConfirmDeleteSubTech(sub, index)} sx={{ color: '#f43f5e' }}>
                            <Delete fontSize="inherit" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {formData.subTechnologies.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="textSecondary italic">{L.noSubItemsDrawer}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>

        <Box p={3} borderTop="1px solid #f1f5f9" display="flex" justifyContent="flex-end" gap={2} bgcolor="#f8fafc">
          <Button
            onClick={() => setDrawerOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTech}
            sx={{
              bgcolor: '#1e3a8a',
              '&:hover': { bgcolor: '#1e3a8a' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 4
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Drawer>

      <ConfirmModal
        open={confirmDialog.open}
        handleClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        handleConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Technologies;
